// Pictures for the level builder's catalog: scenery models and pieces (obstacles, piece kinds) drawn
// with a small three.js renderer of their own. Only while the catalog is open, one picture at a
// time, and only for tiles that scroll into view; each is drawn once per session.

import * as THREE from 'three';
import { sceneryModel } from '../assets/kenney';
import type { LevelBox } from '../maps/boxlevel/types';
import { makeSourceSpace } from '../render/coords';
import { levelMaterial } from '../render/proceduralTextures';
import { shapeGeometry } from '../render/shapeGeometry';

const SIZE = 128;
/** Camera directions (three.js space): models from the front-side, pieces from higher up. */
const MODEL_VIEW = new THREE.Vector3(0.9, 0.55, 1.2).normalize();
const PIECE_VIEW = new THREE.Vector3(0.55, 1.1, 1).normalize();

export class Thumbnails {
  private renderer: THREE.WebGLRenderer | null = null;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(28, 1, 1, 200000);
  private readonly cache = new Map<string, Promise<string | null>>();
  private queue: Promise<unknown> = Promise.resolve();

  constructor(private readonly anisotropy: number) {
    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x8a8478, 2.6));
    const sun = new THREE.DirectionalLight(0xfff6e8, 2.4);
    sun.position.set(1, 2, 1.4);
    this.scene.add(sun);
    const fill = new THREE.DirectionalLight(0xdfe8ff, 0.9);
    fill.position.set(-1.2, 0.6, -0.6);
    this.scene.add(fill);
  }

  /** A scenery model, by manifest id. */
  model(id: string): Promise<string | null> {
    return this.cached('m:' + id, async () => {
      const m = await sceneryModel(id);
      return m ? this.shot(m.clone(), MODEL_VIEW) : null;
    });
  }

  /** Level pieces (Source space), under `key`; `boxes` is only called when it isn't drawn yet. */
  pieces(key: string, boxes: () => LevelBox[]): Promise<string | null> {
    return this.cached('p:' + key, async () => {
      const g = new THREE.Group();
      makeSourceSpace(g);
      const geos: THREE.BufferGeometry[] = [];
      for (const b of boxes()) {
        const size = [b.max[0] - b.min[0], b.max[1] - b.min[1], b.max[2] - b.min[2]];
        const c = [(b.min[0] + b.max[0]) / 2, (b.min[1] + b.max[1]) / 2, (b.min[2] + b.max[2]) / 2];
        const geo = shapeGeometry(b.shape ?? 'box', size, c, b.turn ?? 0, true);
        geos.push(geo);
        g.add(new THREE.Mesh(geo, levelMaterial(b.material, b.color, this.anisotropy)));
      }
      const url = this.shot(g, PIECE_VIEW);
      for (const geo of geos) geo.dispose();
      return url;
    });
  }

  private cached(key: string, make: () => Promise<string | null>): Promise<string | null> {
    let p = this.cache.get(key);
    if (!p) {
      // One at a time, with a breath between pictures so the builder keeps drawing smoothly.
      p = this.queue.then(() => new Promise((r) => setTimeout(r, 0))).then(make, make);
      this.queue = p.catch(() => null);
      this.cache.set(
        key,
        p.catch(() => null),
      );
    }
    return p;
  }

  /** Frame `obj` from `dir` and take the picture (a PNG data URL). */
  private shot(obj: THREE.Object3D, dir: THREE.Vector3): string {
    if (!this.renderer) {
      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
      this.renderer.setSize(SIZE, SIZE, false);
      this.renderer.setClearColor(0x000000, 0);
      this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    }
    this.scene.add(obj);
    obj.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(obj);
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    const r = Math.max(sphere.radius, 1e-3);
    // The bounding sphere is loose round most models: a little closer fills the picture.
    const dist = (r / Math.sin(THREE.MathUtils.degToRad(this.camera.fov / 2))) * 0.82;
    this.camera.position.copy(sphere.center).addScaledVector(dir, dist);
    this.camera.near = dist / 100;
    this.camera.far = dist * 4;
    this.camera.updateProjectionMatrix();
    this.camera.lookAt(sphere.center);
    this.renderer.render(this.scene, this.camera);
    const url = this.renderer.domElement.toDataURL('image/png');
    this.scene.remove(obj);
    return url;
  }
}
